/**
 * Saved starred lists UI for the compare view:
 *  - SaveStarredListButton: "★ Save list (N)" + a small name popover.
 *  - SavedListsPanel: "★ Saved lists" dropdown to browse/restore/rename/delete.
 *
 * Both are per-user (useSavedLists → /api/saved-lists). Each saved list keeps
 * the search it came from so old shortlists are recognizable.
 */
import { useEffect, useRef, useState } from 'react';
import { useSavedLists } from '../hooks/useSavedLists';
import { describeSearch, relativeTime } from '../utils/describeSearch';
import type { SavedListDrug, SavedStarredList, SearchDescriptor } from '../types';

const MODE_BADGE: Record<string, string> = {
  keyword: 'bg-indigo-50 text-indigo-700 ring-indigo-200',
  main: 'bg-emerald-50 text-emerald-700 ring-emerald-200',
  letter: 'bg-amber-50 text-amber-700 ring-amber-200',
};

function drugLabel(d: SavedListDrug): string {
  return d.brand_name || d.generic_name || `Drug ${d.drug_id}`;
}

/** Save the current starred set as a named list. */
export function SaveStarredListButton({
  drugs,
  searchContext,
  defaultName,
  disabled,
  label = 'Save Starred List',
  editListId = null,
  editListName = null,
  preserveContextOnUpdate = false,
}: {
  drugs: SavedListDrug[];
  searchContext: SearchDescriptor;
  defaultName: string;
  disabled?: boolean;
  /** Button text (the popover header stays "Save starred list"). */
  label?: string;
  /** When editing a RESTORED list: its id (→ "Update list" replaces it in
   *  place instead of creating a new one) + its existing name. */
  editListId?: number | null;
  editListName?: string | null;
  /** Update omits search_context so the stored list's context is kept
   *  (backend ignores a null context). "Save as new" is unaffected. */
  preserveContextOnUpdate?: boolean;
}) {
  const { saveList, updateList, saving } = useSavedLists();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(editListName || defaultName);
  const [done, setDone] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isEditing = editListId != null;

  useEffect(() => {
    if (open) setName(editListName || defaultName);
  }, [open, defaultName, editListName]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // saveAsNew=true forces a brand-new list even when editing an existing one.
  const submit = async (saveAsNew = false) => {
    const n = name.trim() || defaultName || 'Starred list';
    if (isEditing && !saveAsNew) {
      await updateList(editListId!, {
        name: n,
        drugs,
        ...(preserveContextOnUpdate ? {} : { search_context: searchContext }),
      });
    } else {
      await saveList({ name: n, drugs, search_context: searchContext });
    }
    setDone(true);
    setTimeout(() => setDone(false), 1800);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="btn-secondary h-8 w-full gap-1.5 whitespace-nowrap px-3 disabled:cursor-not-allowed"
        title={isEditing ? 'Update this saved list with the current starred drugs' : 'Save the starred (★) drugs as a list you can find later'}
      >
        <span className="text-amber-500">★</span>
        {done ? 'Saved ✓' : isEditing ? 'Update list' : label}
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-72 rounded-lg border border-slate-200 bg-white p-3 shadow-lg">
          <div className="mb-1 text-xs font-medium uppercase tracking-wider text-slate-500">
            {isEditing ? 'Update saved list' : 'Save starred list'}
          </div>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') submit();
              if (e.key === 'Escape') setOpen(false);
            }}
            autoFocus
            placeholder="List name"
            className="w-full rounded-md border border-slate-200 px-2.5 py-1.5 text-sm focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-100"
          />
          <div className="mt-1.5 text-[11px] text-slate-400">
            {drugs.length} drug{drugs.length === 1 ? '' : 's'} · from your {describeSearch(searchContext).modeLabel} search
          </div>
          <div className="mt-2 flex items-center justify-end gap-2">
            <button onClick={() => setOpen(false)} className="mr-auto rounded px-2.5 py-1 text-sm text-slate-500 hover:bg-slate-100">
              Cancel
            </button>
            {isEditing && (
              <button
                onClick={() => submit(true)}
                disabled={saving || !drugs.length}
                className="rounded-md border border-slate-200 px-2.5 py-1 text-sm text-slate-600 hover:bg-slate-50 disabled:opacity-50"
                title="Create a separate new list instead of updating this one"
              >
                Save as new
              </button>
            )}
            <button
              onClick={() => submit(false)}
              disabled={saving || !drugs.length}
              className="rounded-md bg-indigo-600 px-3 py-1 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'Saving…' : isEditing ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/** Browse / restore / rename / delete the user's saved lists. */
export function SavedListsPanel({ onRestore }: { onRestore: (list: SavedStarredList) => void }) {
  const { lists, isLoading, renameList, deleteList } = useSavedLists();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<number | null>(null);
  const [draft, setDraft] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="btn-secondary h-[34px] px-3 text-[13px] font-normal"
        title="Your saved starred lists"
      >
        Starred lists
        {lists.length > 0 && (
          <span className="font-mono text-[12px] text-ink-3">{lists.length}</span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 z-30 mt-1 w-[30rem] max-w-[92vw] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg">
          <div className="border-b border-slate-100 bg-slate-50 px-3 py-2 text-xs font-medium uppercase tracking-wider text-slate-500">
            Saved starred lists
          </div>
          {isLoading && lists.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-slate-400">Loading…</div>
          ) : lists.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-slate-400">
              No saved lists yet. Star drugs, then “★ Save list”.
            </div>
          ) : (
            <ul className="max-h-96 overflow-y-auto">
              {lists.map((list) => {
                const d = list.search_context ? describeSearch(list.search_context) : null;
                const names = list.drugs.map(drugLabel);
                const shown = names.slice(0, 3).join(' · ');
                const extra = names.length > 3 ? ` · +${names.length - 3}` : '';
                return (
                  <li key={list.id} className="group border-b border-slate-50 last:border-0 hover:bg-slate-50">
                    {editing === list.id ? (
                      <div className="flex items-center gap-2 px-3 py-2.5">
                        <input
                          value={draft}
                          onChange={(e) => setDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              renameList(list.id, draft.trim() || list.name);
                              setEditing(null);
                            }
                            if (e.key === 'Escape') setEditing(null);
                          }}
                          autoFocus
                          className="flex-1 rounded border border-slate-200 px-2 py-1 text-sm focus:border-indigo-400 focus:outline-none"
                        />
                        <button
                          onClick={() => { renameList(list.id, draft.trim() || list.name); setEditing(null); }}
                          className="rounded bg-indigo-600 px-2 py-1 text-xs font-medium text-white hover:bg-indigo-700"
                        >
                          Save
                        </button>
                        <button onClick={() => setEditing(null)} className="px-1 text-xs text-slate-400 hover:text-slate-600">
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-start">
                        <button
                          type="button"
                          onClick={() => { onRestore(list); setOpen(false); }}
                          className="min-w-0 flex-1 px-3 py-2.5 text-left"
                          title="Reopen these drugs in compare"
                        >
                          <div className="flex items-center gap-2">
                            <span className="truncate text-sm font-semibold text-slate-800">{list.name}</span>
                            <span className="shrink-0 text-[11px] text-slate-400">
                              {list.drugs.length} drug{list.drugs.length === 1 ? '' : 's'} · {relativeTime(list.created_at)}
                            </span>
                          </div>
                          <div className="mt-0.5 truncate text-xs text-slate-500">{shown}{extra}</div>
                          {d && (
                            <div className="mt-1 flex items-center gap-1.5">
                              <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ring-1 ${MODE_BADGE[d.mode] ?? MODE_BADGE['main']}`}>
                                {d.modeLabel}
                              </span>
                              <span className="truncate text-[11px] text-slate-400">{d.text}</span>
                            </div>
                          )}
                        </button>
                        <div className="flex shrink-0 items-center gap-0.5 px-2 py-2.5 opacity-0 transition-opacity group-hover:opacity-100">
                          <button
                            onClick={() => { setEditing(list.id); setDraft(list.name); }}
                            className="rounded px-1.5 py-1 text-slate-400 hover:bg-slate-200 hover:text-slate-700"
                            title="Rename"
                          >
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => deleteList(list.id)}
                            className="rounded px-1.5 py-1 text-slate-400 hover:text-red-500"
                            title="Delete"
                          >
                            <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
